import { useEffect, useMemo, useState } from "react";

import { Enrichment, EnrichmentCombatMeta, MetricBlock, api } from "../api";
import Spinner from "../components/Spinner";
import { useReport } from "../context/ReportContext";
import { CLASSES, classFor } from "../data/classes";
import type { Cast } from "../lib/combatLogParser";

type CombatSel = number | "all";
type Layout = "pivot" | "list" | "summary";

const DEFAULT_FIRST_N = 6;
const MAX_FIRST_N = 30;

// A combat is "real" if it's a boss pull, or had a decent chunk of the raid
// actually pressing buttons. Critter kills, fish that evade at Lurker, single
// stray mobs, etc. fall well below this and are hidden by default.
const MIN_PLAYERS = 8;
const MIN_CASTS = 15;

function isRealCombat(c: EnrichmentCombatMeta): boolean {
  if (c.encounterId) return true;
  return c.playerCount >= MIN_PLAYERS && c.castCount >= MIN_CASTS;
}

/**
 * Per-combat GCD timeline. Fetches the report's combat-log enrichment and
 * renders, for each player, the spells they pressed in order with the seconds
 * between presses. Filterable by combat, class (multi-select), and a "first N
 * GCDs" cutoff; switchable between a player×GCD pivot, a flat per-cast list,
 * and a Summary (most-common spell per slot across all combats).
 *
 * The `block` prop is the synthetic section header from Report.tsx — the data
 * itself is fetched here from /reports/{slug}/enrichment.
 */
export default function CombatLog(_props: { block: MetricBlock }) {
  const { report } = useReport();
  const slug = report?.slug;

  const [data, setData] = useState<Enrichment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getEnrichment(slug)
      .then((d) => !cancelled && setData(d))
      .catch((e) =>
        cancelled
          ? null
          : setError(e instanceof Error ? e.message : "Failed to load"),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) return <Spinner label="Loading enriched data…" />;
  if (error) return <p className="text-sm text-red-400">{error}</p>;
  if (!data || data.combats.length === 0)
    return <p className="text-sm text-gray-500">No enriched data.</p>;

  return <CombatLogView data={data} />;
}

function CombatLogView({ data }: { data: Enrichment }) {
  const allCombats = data.combats;
  const [showJunk, setShowJunk] = useState(false);
  const [selected, setSelected] = useState<CombatSel>(
    () => allCombats.find(isRealCombat)?.id ?? allCombats[0]?.id ?? "all",
  );
  const [firstN, setFirstN] = useState(DEFAULT_FIRST_N);
  const [layout, setLayout] = useState<Layout>("pivot");
  const [classFilter, setClassFilter] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  const keptCombats = useMemo(
    () => (showJunk ? allCombats : allCombats.filter(isRealCombat)),
    [allCombats, showJunk],
  );
  const hiddenCount = allCombats.length - keptCombats.length;

  // Classes actually present in this raid — keeps the filter chips tidy.
  const presentClasses = useMemo(() => {
    const set = new Set<string>();
    for (const e of Object.values(data.roster)) {
      if (e.class) set.add(e.class);
    }
    return Object.keys(CLASSES).filter((c) => set.has(c));
  }, [data.roster]);

  const classOf = (name: string): string | undefined =>
    data.roster[name]?.class ?? undefined;

  const passesFilters = (player: string): boolean => {
    const cls = classOf(player);
    if (classFilter.size && (!cls || !classFilter.has(cls))) return false;
    const q = query.trim().toLowerCase();
    if (q && !player.toLowerCase().includes(q)) return false;
    return true;
  };

  const toggleClass = (c: string) =>
    setClassFilter((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  // Per-(combat, player) rows for the pivot / list layouts.
  const rows = useMemo(() => {
    const out: PlayerRow[] = [];
    const ids = selected === "all" ? keptCombats.map((c) => c.id) : [selected];
    for (const id of ids) {
      const combat = data.combatData[String(id)];
      const meta = allCombats.find((c) => c.id === id);
      if (!combat || !meta) continue;
      for (const [player, casts] of Object.entries(combat.casts)) {
        if (!passesFilters(player)) continue;
        out.push({
          combatId: id,
          label: meta.label,
          kill: meta.kill,
          isBoss: !!meta.encounterId,
          player,
          cls: classOf(player),
          casts,
        });
      }
    }
    out.sort(
      (a, b) => a.combatId - b.combatId || a.player.localeCompare(b.player),
    );
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, keptCombats, allCombats, data.combatData, classFilter, query]);

  // Aggregate across all kept combats for the Summary layout.
  const summary = useMemo(
    () => buildSummary(keptCombats, data, firstN, passesFilters, classOf),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keptCombats, data, firstN, classFilter, query],
  );

  const showCombatCol = selected === "all";

  // Keep the dropdown showing the current selection even if it's a hidden one.
  const combatOptions = useMemo(() => {
    if (showJunk || selected === "all") return keptCombats;
    const extra = allCombats.find(
      (c) => c.id === selected && !keptCombats.includes(c),
    );
    return extra ? [...keptCombats, extra] : keptCombats;
  }, [keptCombats, allCombats, selected, showJunk]);

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
        {layout !== "summary" && (
          <Field label="Combat">
            <select
              value={String(selected)}
              onChange={(e) =>
                setSelected(
                  e.target.value === "all" ? "all" : Number(e.target.value),
                )
              }
              className="rounded border border-border bg-panel px-2 py-1 text-sm outline-none focus:border-accent"
            >
              <option value="all">All combats ({keptCombats.length})</option>
              {combatOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                  {c.encounterId
                    ? c.kill === true
                      ? " (kill)"
                      : c.kill === false
                        ? " (wipe)"
                        : ""
                    : ""}{" "}
                  · {c.durationS}s
                </option>
              ))}
            </select>
          </Field>
        )}

        <Field label={`First ${firstN} GCDs`}>
          <input
            type="range"
            min={1}
            max={MAX_FIRST_N}
            value={firstN}
            onChange={(e) => setFirstN(Number(e.target.value))}
            className="w-36 accent-accent"
          />
        </Field>

        <Field label="Layout">
          <div className="flex rounded border border-border overflow-hidden text-sm">
            <Toggle
              active={layout === "pivot"}
              onClick={() => setLayout("pivot")}
              label="Pivot"
            />
            <Toggle
              active={layout === "list"}
              onClick={() => setLayout("list")}
              label="List"
            />
            <Toggle
              active={layout === "summary"}
              onClick={() => setLayout("summary")}
              label="Summary"
            />
          </div>
        </Field>

        <Field label="Player">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="w-32 rounded border border-border bg-panel px-2 py-1 text-sm outline-none focus:border-accent"
          />
        </Field>
      </div>

      {/* Class filter chips */}
      {presentClasses.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {presentClasses.map((c) => {
            const info = CLASSES[c];
            const on = classFilter.has(c);
            return (
              <button
                key={c}
                onClick={() => toggleClass(c)}
                className="rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors"
                style={{
                  borderColor: info.color + (on ? "" : "55"),
                  background: on ? info.color : "transparent",
                  color: on ? "#0e1116" : info.color,
                }}
              >
                {info.name}
              </button>
            );
          })}
          {classFilter.size > 0 && (
            <button
              onClick={() => setClassFilter(new Set())}
              className="ml-1 text-xs text-gray-500 hover:text-gray-300"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Junk-combat toggle */}
      {hiddenCount > 0 || showJunk ? (
        <label className="flex items-center gap-2 text-xs text-gray-500 select-none">
          <input
            type="checkbox"
            checked={showJunk}
            onChange={(e) => setShowJunk(e.target.checked)}
            className="accent-accent"
          />
          Show low-activity combats
          {!showJunk && hiddenCount > 0 && (
            <span className="text-gray-600">
              ({hiddenCount} hidden — critters, evades, stray pulls)
            </span>
          )}
        </label>
      ) : null}

      {layout === "summary" ? (
        summary.length === 0 ? (
          <p className="text-sm text-gray-500">No casts match these filters.</p>
        ) : (
          <SummaryTable
            rows={summary}
            firstN={firstN}
            combatCount={keptCombats.length}
          />
        )
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No casts match these filters.</p>
      ) : layout === "pivot" ? (
        <PivotTable rows={rows} firstN={firstN} showCombatCol={showCombatCol} />
      ) : (
        <ListTable rows={rows} firstN={firstN} showCombatCol={showCombatCol} />
      )}
    </div>
  );
}

interface PlayerRow {
  combatId: number;
  label: string;
  kill: boolean | null;
  isBoss: boolean;
  player: string;
  cls?: string;
  casts: Cast[];
}

/* ----------------------------- Summary ----------------------------- */

interface SummarySlot {
  top: string; // most-common spell name in this slot
  pct: number; // share of this player's casts at this slot (0-100)
  n: number; // total casts observed at this slot
}
interface SummaryRow {
  player: string;
  cls?: string;
  combats: number; // # kept combats this player pressed something in
  slots: (SummarySlot | null)[];
}

function buildSummary(
  combats: EnrichmentCombatMeta[],
  data: Enrichment,
  firstN: number,
  passes: (player: string) => boolean,
  classOf: (player: string) => string | undefined,
): SummaryRow[] {
  // player -> { combats, slots: [ Map<spell, count> ] }
  const acc = new Map<
    string,
    { combats: number; slots: Map<string, number>[] }
  >();
  for (const meta of combats) {
    const cd = data.combatData[String(meta.id)];
    if (!cd) continue;
    for (const [player, casts] of Object.entries(cd.casts)) {
      if (!passes(player)) continue;
      let entry = acc.get(player);
      if (!entry) {
        entry = {
          combats: 0,
          slots: Array.from(
            { length: firstN },
            () => new Map<string, number>(),
          ),
        };
        acc.set(player, entry);
      }
      entry.combats += 1;
      for (const cast of casts) {
        if (cast.g >= firstN) break;
        const m = entry.slots[cast.g];
        m.set(cast.s, (m.get(cast.s) ?? 0) + 1);
      }
    }
  }

  const out: SummaryRow[] = [];
  for (const [player, entry] of acc) {
    const slots = entry.slots.map<SummarySlot | null>((m) => {
      if (m.size === 0) return null;
      let top = "";
      let topCount = 0;
      let total = 0;
      for (const [spell, count] of m) {
        total += count;
        if (count > topCount) {
          topCount = count;
          top = spell;
        }
      }
      return { top, pct: Math.round((topCount / total) * 100), n: total };
    });
    out.push({ player, cls: classOf(player), combats: entry.combats, slots });
  }
  out.sort(
    (a, b) =>
      (a.cls ?? "z").localeCompare(b.cls ?? "z") ||
      a.player.localeCompare(b.player),
  );
  return out;
}

function SummaryTable({
  rows,
  firstN,
  combatCount,
}: {
  rows: SummaryRow[];
  firstN: number;
  combatCount: number;
}) {
  const cols = Array.from({ length: firstN }, (_, i) => i + 1);
  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500">
        Most-common spell each player presses in each GCD slot, across{" "}
        {combatCount} combat{combatCount === 1 ? "" : "s"}. The small % is how
        often that slot was that spell.
      </p>
      <div className="overflow-x-auto rounded border border-border/60 bg-bg/40">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 font-medium">Player</th>
              <th className="px-3 py-2 font-medium text-right">Combats</th>
              {cols.map((n) => (
                <th key={n} className="px-3 py-2 font-medium text-center">
                  {n}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.player}
                className="border-t border-border/40 hover:bg-white/5"
              >
                <td
                  className="px-3 py-1.5 whitespace-nowrap font-medium"
                  style={{ color: classFor(r.cls).color }}
                >
                  {r.player}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                  {r.combats}
                </td>
                {r.slots.map((s, i) => (
                  <td
                    key={i}
                    className="px-3 py-1.5 text-center align-top border-l border-border/30"
                  >
                    {s ? (
                      <div className="leading-tight">
                        <div className="text-xs text-gray-100 whitespace-nowrap">
                          {s.top}
                        </div>
                        <div className="text-[10px] tabular-nums text-gray-500">
                          {s.pct}%
                        </div>
                      </div>
                    ) : (
                      <Empty />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Pivot ----------------------------- */

function PivotTable({
  rows,
  firstN,
  showCombatCol,
}: {
  rows: PlayerRow[];
  firstN: number;
  showCombatCol: boolean;
}) {
  const cols = Array.from({ length: firstN }, (_, i) => i + 1);
  return (
    <div className="overflow-x-auto rounded border border-border/60 bg-bg/40">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
            {showCombatCol && <th className="px-3 py-2 font-medium">Combat</th>}
            <th className="px-3 py-2 font-medium">Player</th>
            {cols.map((n) => (
              <th key={n} className="px-3 py-2 font-medium text-center">
                {n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const color = classFor(r.cls).color;
            return (
              <tr
                key={`${r.combatId}-${r.player}-${i}`}
                className="border-t border-border/40 hover:bg-white/5"
              >
                {showCombatCol && (
                  <td className="px-3 py-1.5 whitespace-nowrap text-xs text-gray-400">
                    {r.label}
                  </td>
                )}
                <td
                  className="px-3 py-1.5 whitespace-nowrap font-medium"
                  style={{ color }}
                >
                  {r.player}
                </td>
                {cols.map((n) => {
                  const cast = r.casts[n - 1];
                  return (
                    <td
                      key={n}
                      className="px-3 py-1.5 text-center align-top border-l border-border/30"
                    >
                      {cast ? <PivotCell cast={cast} /> : <Empty />}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PivotCell({ cast }: { cast: Cast }) {
  return (
    <div className="leading-tight">
      <div className="text-xs text-gray-100 whitespace-nowrap">{cast.s}</div>
      <div className="text-[10px] tabular-nums text-gray-500">
        {cast.d == null ? `@${secs(cast.t)}` : `+${secs(cast.d)}`}
      </div>
    </div>
  );
}

function Empty() {
  return <span className="text-gray-700">·</span>;
}

/* ----------------------------- List ----------------------------- */

function ListTable({
  rows,
  firstN,
  showCombatCol,
}: {
  rows: PlayerRow[];
  firstN: number;
  showCombatCol: boolean;
}) {
  // Flatten to one entry per cast, honoring the firstN cutoff.
  const flat = useMemo(() => {
    const out: {
      key: string;
      combatLabel: string;
      player: string;
      cls?: string;
      gcd: number;
      cast: Cast;
    }[] = [];
    for (const r of rows) {
      for (const cast of r.casts) {
        if (cast.g >= firstN) break;
        out.push({
          key: `${r.combatId}-${r.player}-${cast.g}`,
          combatLabel: r.label,
          player: r.player,
          cls: r.cls,
          gcd: cast.g + 1,
          cast,
        });
      }
    }
    return out;
  }, [rows, firstN]);

  return (
    <div className="overflow-x-auto rounded border border-border/60 bg-bg/40">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-500">
            {showCombatCol && <th className="px-3 py-2 font-medium">Combat</th>}
            <th className="px-3 py-2 font-medium">Player</th>
            <th className="px-3 py-2 font-medium text-right">#</th>
            <th className="px-3 py-2 font-medium">Spell</th>
            <th className="px-3 py-2 font-medium text-right">t</th>
            <th className="px-3 py-2 font-medium text-right">Δ</th>
          </tr>
        </thead>
        <tbody>
          {flat.map((f) => (
            <tr
              key={f.key}
              className="border-t border-border/40 hover:bg-white/5"
            >
              {showCombatCol && (
                <td className="px-3 py-1 whitespace-nowrap text-xs text-gray-400">
                  {f.combatLabel}
                </td>
              )}
              <td
                className="px-3 py-1 whitespace-nowrap font-medium"
                style={{ color: classFor(f.cls).color }}
              >
                {f.player}
              </td>
              <td className="px-3 py-1 text-right tabular-nums text-gray-500">
                {f.gcd}
              </td>
              <td className="px-3 py-1 whitespace-nowrap text-gray-100">
                {f.cast.s}
              </td>
              <td className="px-3 py-1 text-right tabular-nums text-gray-400">
                {secs(f.cast.t)}
              </td>
              <td className="px-3 py-1 text-right tabular-nums text-gray-400">
                {f.cast.d == null ? "—" : `+${secs(f.cast.d)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------- bits ----------------------------- */

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wide text-gray-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function Toggle({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "px-3 py-1 transition-colors " +
        (active
          ? "bg-accent/15 text-accent"
          : "text-gray-400 hover:bg-white/5 hover:text-gray-200")
      }
    >
      {label}
    </button>
  );
}

/** ms -> "N.Ns" with one decimal. */
function secs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}
